class ScrollVideo {
    constructor() {
        this.init();
    }

    init() {
        // Wait for DOM to be ready and GSAP to be available
        const checkGSAP = () => {
            if (typeof window.gsap !== "undefined") {
                this.setupScrollAnimations();
            } else {
                // Wait a bit and try again
                setTimeout(checkGSAP, 100);
            }
        };

        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", checkGSAP);
        } else {
            checkGSAP();
        }
    }

    setupScrollAnimations() {
        const scrollVideoElements = document.querySelectorAll(
            "[data-scroll-video]"
        );

        if (scrollVideoElements.length === 0) return;

        scrollVideoElements.forEach((container, index) => {
            const videoElement = container.querySelector(
                "[data-scroll-video-element]"
            );
            if (!videoElement || videoElement.tagName !== "VIDEO") {
                return;
            }

            const trigger =
                container.getAttribute("data-scroll-trigger") || "scrub";
            const scrollDistance =
                parseInt(container.getAttribute("data-scroll-duration")) || 600;

            // Handle both old (number) and new (string) formats for scroll triggers
            let startTrigger =
                container.getAttribute("data-scroll-start") || "top 50";
            let endTrigger =
                container.getAttribute("data-scroll-end") || "+=600 50";

            // Convert old number format to new string format
            if (startTrigger && !isNaN(parseInt(startTrigger))) {
                startTrigger = `top ${startTrigger}%`;
            }
            if (endTrigger && !isNaN(parseInt(endTrigger))) {
                endTrigger = `+=${scrollDistance} ${endTrigger}%`;
            }

            const shouldPin =
                container.getAttribute("data-scroll-pin") === "true";
            const pinHeight =
                container.getAttribute("data-pin-height") || "100";

            if (trigger === "scrub") {
                this.setupVideoScrubbing(
                    container,
                    videoElement,
                    scrollDistance,
                    startTrigger,
                    endTrigger,
                    shouldPin,
                    pinHeight,
                    index
                );
            }
        });
    }

    setupVideoScrubbing(
        container,
        videoElement,
        scrollDistance,
        startTrigger,
        endTrigger,
        shouldPin,
        pinHeight,
        index
    ) {
        const src = videoElement.currentSrc || videoElement.src;

        // Ensure video is muted and ready (like CodePen)
        videoElement.muted = true;
        videoElement.playsInline = true;
        videoElement.pause(); // Don't autoplay - control via scroll

        // Wait for video to be ready before creating ScrollTrigger
        if (videoElement.readyState < 2) {
            // console.log("Video not ready, waiting for loadedmetadata...");
            this.once(videoElement, "loadedmetadata", () => {
                this.createVideoScrollTrigger(
                    container,
                    videoElement,
                    scrollDistance,
                    startTrigger,
                    endTrigger,
                    shouldPin,
                    pinHeight
                );
            });
            return;
        }

        // console.log("Video ready state:", videoElement.readyState);
        // console.log("Video duration:", videoElement.duration);

        this.createVideoScrollTrigger(
            container,
            videoElement,
            scrollDistance,
            startTrigger,
            endTrigger,
            shouldPin,
            pinHeight
        );

        // Make sure the video is 'activated' on iOS
        this.once(document.documentElement, "touchstart", function (e) {
            videoElement.play();
            videoElement.pause();
        });
    }

    createVideoScrollTrigger(
        container,
        videoElement,
        scrollDistance,
        startTrigger,
        endTrigger,
        shouldPin,
        pinHeight
    ) {
        // Validate container element
        if (!container || !(container instanceof Element)) {
            return;
        }

        // Create the scroll trigger timeline like CodePen
        if (typeof window.gsap === "undefined") {
            // console.log("GSAP not available");
            return;
        }

        // Check the pin element
        const pinElement = shouldPin ? container : false;

        // Check the scroller
        const scrollerElement = window.smoother || window;

        // Ensure ScrollSmoother is fully initialized before creating ScrollTrigger
        if (
            window.smoother &&
            typeof window.smoother.scrollTop !== "function"
        ) {
            // console.log("ScrollSmoother not ready, retrying in 100ms");
            setTimeout(() => {
                this.createVideoScrollTrigger(
                    container,
                    videoElement,
                    scrollDistance,
                    startTrigger,
                    endTrigger,
                    shouldPin,
                    pinHeight
                );
            }, 100);
            return;
        }

        // Use the correct scroller based on GSAP documentation
        let scrollTriggerConfig;

        if (window.smoother) {
            // When ScrollSmoother is active, don't specify scroller and let ScrollTrigger handle it
            scrollTriggerConfig = {
                trigger: container,
                start: startTrigger,
                end: endTrigger,
                scrub: true,
                pin: pinElement,
                pinSpacing: shouldPin,
            };
        } else {
            // Fallback to window when ScrollSmoother is not available
            scrollTriggerConfig = {
                trigger: container,
                start: startTrigger,
                end: endTrigger,
                scrub: true,
                pin: pinElement,
                pinSpacing: shouldPin,
                scroller: window,
            };
        }

        const videoScrub = window.gsap.timeline({
            currentTime: videoElement.duration,
            scrollTrigger: scrollTriggerConfig,
        });

        // Add the video scrubbing animation
        videoScrub.fromTo(
            videoElement,
            { currentTime: 0 },
            { currentTime: videoElement.duration || 1 }
        );
    }

    once(el, event, fn, opts) {
        var onceFn = function (e) {
            el.removeEventListener(event, onceFn);
            fn.apply(this, arguments);
        };
        el.addEventListener(event, onceFn, opts);
        return onceFn;
    }
}

// Initialize scroll video functionality
new ScrollVideo();

export { ScrollVideo };
